import { useEffect, useState } from "react";
import ContainerModalActions from "../Containers/ContainerModalActions";
import {
  BUTTON_CREATE,
  BUTTON_GO_BACK,
  BUTTON_SELECT,
  BUTTON_UPDATE,
  LOADING_INFORMATION,
  MODAL_INSTANT_FORMS_LIST_DESCRIPTION,
  MODAL_INSTANT_FORMS_LIST_TITLE,
  MODAL_LEAD_FORMS_COPY_NAME_PREFIX,
  UPDATING_INFORMATION,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import ModalBasicLayout from "../Modal/ModalBasicLayout";
import Loader from "../Loaders/Loader";
import Box from "@mui/material/Box";
import DialogActions from "@mui/material/DialogActions";
import { useDispatchApp } from "../../lib/redux";
import { usePagination } from "../../hooks/usePagination";
import useNearScreen from "../../hooks/useNearScreen";
import { startGetInstantForm } from "../../actions/adsCampaigns";
import { InstantFormsList } from "./InstantFormsList";
import _ from "lodash";
import Button from "../Buttons/Button";
import { META } from "../../utils/constants";
import ModalCreateInstantForm from "./ModalCreateInstantForm";

const ModalInstantFormsList = ({
  modalOpen,
  integrationID,
  pageID,
  onCloseModal = () => {},
  onCallbackSelect = () => {},
  createInitialValues = null,
  leadFormRecommendation = null,
}) => {
  const [instantForms, setInstantForms] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [duplicateValues, setDuplicateValues] = useState(null);
  const [duplicateFromId, setDuplicateFromId] = useState(null);
  const [isViewOnly, setIsViewOnly] = useState(false);

  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();

  const onGetInstantForms = async () => {
    if (loadLastItem) return;

    const response = await dispatch(
      startGetInstantForm({
        integrationID,
        pageID,
        lastKey,
        onChangeLastKey,
        onChangeLoadLastItem,
        platform: META,
      })
    );

    if (response.ok) {
      const data = response.data;

      setInstantForms((prev) => _.uniqBy([...prev, ...data], "id"));
    }

    if (isLoading) setIsLoading(false);

    return response.ok;
  };

  const { lastKey, onChangeLastKey } = usePagination({
    rowsPerPageValue: 10,
    onCallBackNextPage: onGetInstantForms,
  });

  const { fromRef, loadLastItem, onChangeLoadLastItem, isNearScreen } =
    useNearScreen({
      distance: "100px",
    });

  const onUpdateInstantForms = async () => {
    setSelectedItem(null);
    setIsUpdating(true);
    setIsLoading(true);

    const response = await dispatch(
      startGetInstantForm({
        integrationID,
        pageID,
        onChangeLastKey,
        onChangeLoadLastItem,
        platform: META,
      })
    );

    if (response.ok) {
      const data = response.data;

      setInstantForms(data);
    }

    setIsLoading(false);
    setIsUpdating(false);

    return response.ok;
  };

  useEffect(() => {
    onGetInstantForms();
    // eslint-disable-next-line
  }, [isNearScreen]);

  const onSendSelectedItem = () => {
    const instantFormSelected = instantForms.find(
      (instantForm) => instantForm.id === selectedItem
    );
    if (!instantFormSelected) return;
    onCallbackSelect({
      id: instantFormSelected.id,
      name: instantFormSelected.name,
      creationTime: instantFormSelected.creationTime,
    });
    onCloseModal();
  };

  const onCreatedInstantForm = (instantForm) => {
    if (!instantForm?.id) return;
    setInstantForms((prev) =>
      _.uniqBy(
        [
          {
            ...instantForm,
            creationTime: instantForm.creationTime || Date.now(),
          },
          ...prev,
        ],
        "id"
      )
    );
    setSelectedItem(instantForm.id);
  };

  const withCopyNamePrefix = (values) => {
    const prefix = t(MODAL_LEAD_FORMS_COPY_NAME_PREFIX);
    const currentName = String(values?.name || "").trim();
    if (!currentName) {
      return { ...values, name: prefix };
    }
    if (currentName.toLowerCase().startsWith(`${prefix.toLowerCase()} `)) {
      return values;
    }
    return {
      ...values,
      name: `${prefix} ${currentName}`,
    };
  };

  const onOpenCreateModal = () => {
    setDuplicateValues(createInitialValues);
    setDuplicateFromId(null);
    setIsViewOnly(false);
    setCreateModalOpen(true);
  };

  const onDuplicateInstantForm = (instantForm) => {
    if (!instantForm?.duplicateValues) return;
    setDuplicateFromId(instantForm.id || null);
    setIsViewOnly(false);
    setDuplicateValues(withCopyNamePrefix(instantForm.duplicateValues));
    setCreateModalOpen(true);
  };

  const onViewInstantForm = (instantForm) => {
    if (!instantForm?.duplicateValues) return;
    setDuplicateFromId(instantForm.id || null);
    setIsViewOnly(true);
    setDuplicateValues(instantForm.duplicateValues);
    setCreateModalOpen(true);
  };

  const onCloseCreateModal = () => {
    setCreateModalOpen(false);
    setDuplicateValues(null);
    setDuplicateFromId(null);
    setIsViewOnly(false);
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      fullWidth={true}
      scroll="paper"
      maxWidth="xs"
      title={t(MODAL_INSTANT_FORMS_LIST_TITLE)}
      description={t(MODAL_INSTANT_FORMS_LIST_DESCRIPTION)}
    >
      {isLoading && (
        <Loader
          hasMessage={true}
          message={t(isUpdating ? UPDATING_INFORMATION : LOADING_INFORMATION)}
        />
      )}
      {!isLoading && (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: {
                xs: "column",
                sm: "row",
              },
              gap: 1,
              justifyContent: "flex-end",
              alignItems: "flex-end",
              width: "100%",
              mb: 2,
            }}
          >
            <Button
              disabled={isLoading}
              color="secondary"
              onClick={onUpdateInstantForms}
              sx={{
                minWidth: 110,
                height: 36,
                px: 1.5,
                py: 0.5,
                fontSize: "13px !important",
              }}
            >
              {t(BUTTON_UPDATE)}
            </Button>
            <Button
              type="button"
              color="secondary"
              onClick={onOpenCreateModal}
              sx={{
                minWidth: 110,
                height: 36,
                px: 1.5,
                py: 0.5,
                fontSize: "13px !important",
              }}
            >
              {t(BUTTON_CREATE)}
            </Button>
          </Box>
          <InstantFormsList
            selectedItem={selectedItem}
            onChangeSelectedItem={setSelectedItem}
            onDuplicateInstantForm={onDuplicateInstantForm}
            onViewInstantForm={onViewInstantForm}
            instantForms={instantForms}
            lastItemRef={fromRef}
            loadLastItem={loadLastItem}
          />
        </>
      )}
      <DialogActions
        sx={{
          p: 0,
        }}
      >
        <ContainerModalActions
          hasPadding={true}
          propsSecondaryButton={{
            showButton: true,
            onClick: onCloseModal,
            text: BUTTON_GO_BACK,
          }}
          propsPrimaryButton={{
            showButton: true,
            disabled: !selectedItem,
            text: BUTTON_SELECT,
            type: "submit",
            onClick: onSendSelectedItem,
          }}
        />
      </DialogActions>
      {createModalOpen && (
        <ModalCreateInstantForm
          key={`${isViewOnly ? "view" : "edit"}-${duplicateFromId || "create"}`}
          modalOpen={createModalOpen}
          onCloseModal={onCloseCreateModal}
          integrationID={integrationID}
          pageID={pageID}
          initialValues={duplicateValues}
          readOnly={isViewOnly}
          leadFormRecommendation={
            !isViewOnly && !duplicateFromId ? leadFormRecommendation : null
          }
          onCallbackSave={onCreatedInstantForm}
        />
      )}
    </ModalBasicLayout>
  );
};

export default ModalInstantFormsList;
